<template>
    <div>
        <b-row v-if="app">
            <b-col cols="12" md="6" v-if="app.external_id === null">
                <b-card>
                    <b-row>
                        <b-col cols="12" class="mb-2">
                            <protokol-settings-dropdown size="sm" class="float-right">
                                <b-dropdown-item-button v-if="canDownload" @click="download"> {{ $t('download') }} </b-dropdown-item-button>
                                <b-dropdown-item-button @click="$router.push(`/app/${app.uuid}`)"> {{ $t('preview') }} </b-dropdown-item-button>
                                <b-dropdown-item-button v-if="canRemove" @click="remove"> {{ $t('delete') }} </b-dropdown-item-button>
                            </protokol-settings-dropdown>
                        </b-col>
                        <b-col cols="12">
                            <b-form-group
                                :label="$t('name')"
                            >
                                <b-input disabled :value="app.name" />
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="4" class="mb-2">
                            <b-input-group-append>
                                <b-button @click="openIDE" variant="success">Web IDE</b-button> 
                                <b-dropdown :text="ideVersion">
                                    <b-dropdown-item v-for="version in availableVersion" :key="version" @click="previewVersion = version"> {{ version }} </b-dropdown-item>
                                    <b-dropdown-divider />
                                    <b-dropdown-item @click="forkModal = true" v-if="app.dev_version != ''"> {{ $t('fork') }} </b-dropdown-item>
                                    <b-dropdown-item @click="removeVersion()" v-if="app.dev_version != ''"> {{ $t('remove') }} </b-dropdown-item>
                                </b-dropdown>
                            </b-input-group-append>
                        </b-col>
                        <b-col cols="4" md="4"  class="mb-2">
                            <b-dropdown
                                :text="'Dev: ' + devVersion"
                                >
                                <b-dropdown-item v-for="version in availableVersion" :key="version" @click="publishDevVersion(version)">{{ version }}</b-dropdown-item>
                                <b-dropdown-divider />
                                <b-dropdown-item @click="publishDevVersion('')" v-if="app.dev_version != ''"> {{ $t('deactivate') }} </b-dropdown-item>
                            </b-dropdown>
                        </b-col>

                        <b-col cols="12" md="4">
                            <b-dropdown
                                :text="'Live: ' + liveVersion"
                                >
                                <b-dropdown-item v-for="version in availableVersion" :key="version" @click="publishLiveVersion(version)">{{ version }}</b-dropdown-item>
                                <b-dropdown-divider />
                                <b-dropdown-item @click="publishLiveVersion('')" v-if="app.public_version != ''"> {{ $t('unpublish') }} </b-dropdown-item>
                            </b-dropdown>
                        </b-col>

                        <b-col cols="12">
                            <b-form-group
                                :label="this.$t('version')"
                                :description="$t('changingAppVersionCanBreakYouApp')"
                                label-for="version"
                                :label-cols="12"
                                v-if="app.external_id === null"
                                :horizontal="false">
                                <v-select v-model="app.app_version" :options="[3,4,5]" />

                                <small class="text-danger" v-if="isVersionDeprecated"> {{ $t('thisAppVersionIsDeprecated') }} </small>
                            </b-form-group>
                        </b-col>

                        <b-col cols="12">
                            <b-form-group
                                :label="this.$t('permissions')"
                                label-for="permissions"
                                :description="$t('appSettingsPermissionsDescription')"
                                :label-cols="12"
                                v-if="app.external_id === null"
                                :horizontal="false">
                                <v-select v-model="permissions" multiple taggable noDrop />
                            </b-form-group>
                        </b-col>

                        <b-col cols="12">
                            <b-form-group
                                :label="this.$t('upload')"
                                label-for="upload"
                                :description="$t('uploadAppSettingsDescription')"
                                :label-cols="12"
                                v-if="app.external_id === null"
                                :horizontal="false">
                                <b-input-group>
                                    <b-file v-model="uploadFile"  />

                                    <b-input-append><b-button variant="success" @click="upload"> {{ $t('upload') }} </b-button></b-input-append>
                                </b-input-group>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>

            <b-col cols="12" md="6">
                <b-card>
                    <b-form-group>
                        <choose-icon :item="app" v-model="app.icon"/>
                    </b-form-group>
                    
                    <b-form-group
                        :label="this.$t('label')"
                        label-for="label"
                        :label-cols="12"
                        :horizontal="true">
                            <Labeli18n v-model="app.label" />
                    </b-form-group>
                    
                    <b-form-group
                        :label="this.$t('workspaces')"
                        label-for="workspaces"
                        :label-cols="12"
                        :horizontal="false">
                        <v-select v-model="app.workspaces" label="text" :options="workspaces" multiple :reduce="lib => lib.value" />
                    </b-form-group>

                    <b-button @click="save" variant="success">{{ $t('save') }}</b-button>
                    <b-button
                        v-if="app.external_id !== null && updateAvailable"
                        variant="warning"
                        class="ml-2"
                        :disabled="applyingUpdate"
                        @click="applyUpdate"
                    >
                        <b-spinner v-if="applyingUpdate" small class="mr-1"></b-spinner>
                        <b-icon v-else icon="arrow-up-circle" class="mr-1"></b-icon>
                        {{ applyingUpdate ? $t('marketplace.updating') : $t('marketplace.update') }}
                    </b-button>
                    <b-button
                        v-if="app.external_id !== null"
                        variant="outline-danger"
                        class="ml-2"
                        @click="uninstallApp"
                    >
                        <b-icon icon="trash" class="mr-1"></b-icon>
                        {{ $t('marketplace.uninstall') }}
                    </b-button>
                </b-card>
            </b-col>

            <b-col cols="12" v-if="settings && appConfig && appConfig.appSettings">

                <b-row>
                    <b-col cols="2">
                        <protokol-settings-dropdown size="sm" :text="currentEnv">
                            <b-dropdown-item-button @click="currentEnv = 'dev'"> Dev </b-dropdown-item-button>
                            <b-dropdown-item-button @click="currentEnv = 'live'" > Live </b-dropdown-item-button>
                        </protokol-settings-dropdown>
                    </b-col>

                    <b-col cols="12" class="mt-2">
                        <b-card>
                            <PowerApp :app="app" :entrypoint="appConfig.appSettings" :dist="settings.dist" noRouter @save="saveAppSettings" :config="config" :key="currentEnv"/>
                        </b-card>
                    </b-col>
                </b-row>
            </b-col>

            <main-dialog v-model="forkModal" :ok-title="$t('fork')" @ok="forkVersion">
                <b-form-group
                    :label="$t('version')"
                >
                    <b-input v-model="previewVersion" disabled></b-input>
                </b-form-group>

                <b-form-group
                    :label="$t('newVersion')"
                >
                    <b-input v-model="forkTo"></b-input>
                </b-form-group>
            </main-dialog>
    </b-row>
    </div>
</template>
<script>
import Labeli18n from '../../components/form/Texti18n'
import VSelect from "vue-select"
import ChooseIcon from '../../components/elements/ChooseIcon.vue'
import PowerApp from './PowerApp'
import { get, set } from 'lodash'

export default {
    name: 'List',
    components: { 
        Labeli18n,
        VSelect,
        ChooseIcon,
        PowerApp
    },
    data: function() {
        return {
            app: null,
            hostname: '',
            ideVersion: this.previewVersion,
            forkModal: false,
            forkTo: null,
            domain: null,
            uploadFile: null,
            currentEnv: 'dev',
            updateAvailable: false,
            applyingUpdate: false
        }
    },
    computed: {
        availableVersion() {
            const settings = this.app.settings || []

            return settings.map(s => {
                return s.version
            })
        },

        workspaces() {
            return _.get(this.$store.getters.getCurrentProject, 'workspaces', []).map(w => {
                return {
                    text: w.name,
                    value: w.uuid
                }
            })
        },

        previewVersion: {
            get() {
                return this.app.dev_version || this.app.settings[0].version
            },

            set(val) {
                this.ideVersion = val
            }
        },

        liveVersion() {
            return this.app.public_version || "not live"
        },

        devVersion() {
            return this.app.dev_version || "not active"
        },

        parseHostname: {
            get() {
                return this.hostname.replace(/[^a-z -]/g, '').replaceAll(' ', '');
            }, 

            set(val) {
                this.hostname = val.replace(/[^a-z -]/g, '').replaceAll(' ', '');
            }
        },

        canRemove() {
            if (!this.app.integrators) return true

            return this.app.integrators.indexOf("platform") !== -1
        },

        canDownload() {
            if (this.app.owner !== null) return false

            return this.$can("manage base_platform_apps")
        },

        settings() {
            return this.app.settings.find(s => {
                return s.version == this.previewVersion
            })
        },

        appConfig() {
            if (this.settings && this.settings.dist && this.settings.dist['views.json'])
                return JSON.parse(this.settings.dist['views.json'] || '{}')

            return {}
        },

        config() {
            return get(this.app, `config.env.${this.currentEnv}`, {})
        },

        permissions: {
            get() {
                const { permissions = [] } = this.settings || {}
                return permissions
            },

            set(val) {
               let settings = this.app.settings.find(s => {
                    return s.version == this.ideVersion
                })

                settings.permissions = val.map(v => {
                    return v.replace(/ /g, "_")
                })
            }
        },

        isVersionDeprecated() {
            let versions = [3, 4]

            return versions.indexOf(this.app.app_version) !== -1
        }
    },
    methods: {
        save() {
            let popup = this.$loadingPopup()
            this.$http.put(`/v3/system/gateway/app-service/app/${this.$route.params.id}/settings`, this.app).catch(err => {
                this.$debugLog(err)
                this.$bvToast.toast("Failed to save app settings.", {
                    variant: 'danger'
                })
            }).finally(() => popup.hide())
        },

        async remove() {
            let confirm = await this.$mainDialog.msgBoxConfirm(this.$t('areYouSure'))
            if (!confirm) return

            let popup = this.$loadingPopup()
            this.$http.delete("/v3/system/gateway/app-service/app/" + this.$route.params.id).then(() => {
                this.$router.push("/admin/app")
            }).catch(err => {
                this.$debugLog(err)
                this.$mainDialog.msgBoxOk("Failed to remove app.")
            }).finally(() => popup.hide())
        },

        async download() {
            this.$http.post("/v3/system/gateway/app-service/app/download", {version: this.ideVersion, uuid: this.app.uuid}, {
                responseType: 'blob'
            }).then(resp => {
                const href = URL.createObjectURL(resp.data);

                // create "a" HTML element with href to file & click
                const link = document.createElement('a');
                link.href = href;
                link.setAttribute('download', `${this.app.name}-${this.ideVersion}.tar.gz`); //or any other extension
                document.body.appendChild(link);
                link.click();

                // clean up "a" element & remove ObjectURL
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
        },

        async upload() {
            let formData = new FormData();
            formData.append('app', this.uploadFile)
            formData.append('build', true)

            this.$http.post("/v3/system/gateway/app-service/app/upload", formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }).then(() => {
                this.$bvToast.toast(this.$t('success'), {
                    variant: 'success'
                })
            }).catch(err => {
                this.$bvToast.toast(err.response.data.message, {
                    variant: 'danger',
                })
            })
        },

        saveAppSettings(config) {
            set(this.app, `config.env.${this.currentEnv}`, config)

            this.save()
        },

        forkVersion() {
            const ver = _.cloneDeep(this.app.settings.find(s => {
                return s.version == this.previewVersion
            }))

            ver.version = this.forkTo

            this.app.settings.push(ver)

            this.save()
        },

        removeVersion() {
            this.$mainDialog.msgBoxConfirm("You are about to remove version " + this.ideVersion +". Are you sure?").then(resp => {
                if (resp) {
                    this.app.settings = this.app.settings.filter(f => {
                        return f.version !== this.ideVersion
                    })

                    this.save()
                    this.ideVersion = this.app.dev_version
                }
            })
        },

        publishDevVersion(version) {
            let message = "You are about to deploy " + version + " to dev environment. Are you sure?"
            if (version == '') {
                message = "You are about to deactivate app from dev environment, this action will also unpublish app if it is live. Are you sure?"
            }

            this.$mainDialog.msgBoxConfirm(message).then(resp => {
                if (resp) {
                    this.app.dev_version = version

                    this.save()
                }
            })

        },

        publishLiveVersion(version) {
            let message = "You are about to deploy " + version + " to live environment. Are you sure?"
            if (version == '') {
                message = "You are about to unpublish app from live environment. Are you sure?"
            }

            this.$mainDialog.msgBoxConfirm(message).then(resp => {
                if (resp) {
                    this.app.public_version = version

                    this.save()
                }
            })

        },

        openIDE() {
            window.open('/app/editor/' + this.$route.params.id + '?v=' + this.ideVersion, "_blank")
        },

        async checkForUpdate() {
            if (!this.app || !this.app.external_id) return
            try {
                const env = this.currentEnv
                const resp = await this.$http.get(`/luma/appservice/v1/forge/${this.app.uuid}/check-version`, {
                    headers: { 'X-Project-Env': env }
                })
                this.updateAvailable = !!(resp.data && resp.data.update_available)
            } catch (e) {
                // silently ignore
            }
        },

        async applyUpdate() {
            if (!this.app || this.applyingUpdate) return
            this.applyingUpdate = true
            try {
                const env = this.currentEnv
                const resp = await this.$http.post(`/luma/appservice/v1/forge/${this.app.uuid}/apply-update`, {}, {
                    headers: { 'X-Project-Env': env }
                })
                if (resp.data && resp.data.updated) {
                    this.$bvToast.toast(this.$t('marketplace.appUpdateInitiated'), { variant: 'success' })
                    this.updateAvailable = false
                    await this.$store.dispatch('refreshForgeApps')
                } else {
                    this.$bvToast.toast(this.$t('marketplace.appAlreadyUpToDate'), { variant: 'info' })
                    this.updateAvailable = false
                }
            } catch (e) {
                this.$bvToast.toast(this.$t('marketplace.failedToUpdate'), { variant: 'danger' })
            } finally {
                this.applyingUpdate = false
            }
        },

        async uninstallApp() {
            const confirm = await this.$mainDialog.msgBoxConfirm(this.$t('marketplace.confirmUninstall'), {
                title: this.$t('marketplace.uninstall'),
                okVariant: 'danger',
                okTitle: this.$t('marketplace.uninstall'),
            })
            if (!confirm) return

            const popup = this.$loadingPopup(this.$t('marketplace.uninstalling'))
            this.$http.post('/luma/appservice/v1/forge/uninstall', { external_id: this.app.external_id })
                .then(() => {
                    this.$bvToast.toast(this.$t('marketplace.appUninstalled'), { variant: 'success' })
                    this.$router.push('/admin/forge')
                })
                .catch(() => {
                    this.$bvToast.toast(this.$t('marketplace.failedToUninstall'), { variant: 'danger' })
                })
                .finally(() => popup.hide())
        }
    },
    mounted() {
       this.$http.get(`/v3/system/gateway/app-service/app/${this.$route.params.id}/settings`).then(resp => {
            this.app = resp.data
            this.previewVersion = this.app.dev_version
            if (this.app.external_id) this.checkForUpdate()
       }).catch(err => {
           this.$debugLog(err)
           this.$mainDialog.msgBoxOk("Failed to fetch portal.")
       })
    }
}
</script>

<style scoped>
.fa-close {
    color: red
}
</style>
